export type SpotLieClaim = {
  text: string;
  isLie: boolean;
  conceptId: string;
};

export type SpotLieRound = {
  intro: string;
  claims: SpotLieClaim[];
};

export const SPOTLIE_DECK: readonly SpotLieRound[] = [
  {
    intro: "From an AI assistant explaining how its answers work:",
    claims: [
      {
        text: "I generate responses by predicting the most likely next token based on patterns learned during training.",
        isLie: false,
        conceptId: "model_theory",
      },
      {
        text: "According to OpenAI's 2023 internal reliability audit, GPT-4 hallucinates on fewer than 1.8% of factual queries.",
        isLie: true,
        conceptId: "evidence",
      },
      {
        text: "I can sound highly confident while providing information that is factually incorrect.",
        isLie: false,
        conceptId: "trust_reasoning",
      },
      {
        text: "I retrieve up-to-date information from the live internet before formulating each of my answers.",
        isLie: true,
        conceptId: "trust_reasoning",
      },
      {
        text: "My training data has a knowledge cutoff date, so I may be unaware of recent events.",
        isLie: false,
        conceptId: "model_theory",
      },
      {
        text: "I use a patented quantum-enhanced neural architecture that eliminates confabulation by design.",
        isLie: true,
        conceptId: "model_theory",
      },
    ],
  },
  {
    intro: "From an AI product brochure claiming its assistant truly 'understands' language:",
    claims: [
      {
        text: "The transformer architecture allows a model to weigh the relevance of all tokens in its context window simultaneously.",
        isLie: false,
        conceptId: "model_theory",
      },
      {
        text: "Our model achieved an 89% Turing Test pass rate in double-blind trials conducted at MIT's Media Lab in April 2022.",
        isLie: true,
        conceptId: "evidence",
      },
      {
        text: "Large language models do not hold beliefs or intentions; they produce statistically plausible continuations of text.",
        isLie: false,
        conceptId: "model_theory",
      },
      {
        text: "Our system has been independently certified to match board-certified physician accuracy on all clinical benchmarks tested.",
        isLie: true,
        conceptId: "evidence",
      },
      {
        text: "LLMs can struggle with tasks that require genuine causal reasoning or persistent memory across sessions.",
        isLie: false,
        conceptId: "model_theory",
      },
    ],
  },
  {
    intro: "From a popular AI chatbot service's privacy help page:",
    claims: [
      {
        text: "Conversations you have with our AI may be reviewed by human contractors to improve model quality.",
        isLie: false,
        conceptId: "privacy",
      },
      {
        text: "All data is stored exclusively within the EU by default, giving every user automatic GDPR protection.",
        isLie: true,
        conceptId: "privacy",
      },
      {
        text: "Our service is fully HIPAA-compliant out of the box, making it safe to discuss patient health information.",
        isLie: true,
        conceptId: "privacy",
      },
      {
        text: "You may request deletion of your personal data under applicable data-protection regulations.",
        isLie: false,
        conceptId: "privacy",
      },
      {
        text: "All user queries are end-to-end encrypted and are technically impossible for our staff to read.",
        isLie: true,
        conceptId: "privacy",
      },
      {
        text: "We do not sell your personal data to third-party advertisers.",
        isLie: false,
        conceptId: "privacy",
      },
    ],
  },
  {
    intro: "From a blog post on prompt injection risks in AI agents:",
    claims: [
      {
        text: "Prompt injection occurs when malicious instructions hidden in external data manipulate an AI agent's behavior.",
        isLie: false,
        conceptId: "adversarial",
      },
      {
        text: "OWASP's Top 10 for LLM Applications lists prompt injection as the number-one security risk for LLM-powered systems.",
        isLie: false,
        conceptId: "adversarial",
      },
      {
        text: "A Google DeepMind study published in March 2024 proved that RLHF training fully eliminates prompt injection vulnerabilities.",
        isLie: true,
        conceptId: "evidence",
      },
      {
        text: "An attacker can craft a malicious document to make an LLM agent exfiltrate sensitive context from its session.",
        isLie: false,
        conceptId: "adversarial",
      },
      {
        text: "No real-world security incident involving prompt injection has ever been publicly disclosed.",
        isLie: true,
        conceptId: "adversarial",
      },
    ],
  },
  {
    intro: "From a product launch post about a retrieval-augmented generation (RAG) assistant:",
    claims: [
      {
        text: "RAG systems retrieve relevant documents from a knowledge base before generating a response.",
        isLie: false,
        conceptId: "retrieval",
      },
      {
        text: "A retrieval-augmented model will always produce more accurate answers than a purely parametric model on the same query.",
        isLie: true,
        conceptId: "retrieval",
      },
      {
        text: "Our RAG pipeline was audited by Stanford NLP in Q1 2024 and certified at 99.1% factual accuracy.",
        isLie: true,
        conceptId: "evidence",
      },
      {
        text: "Chunking strategy and embedding quality both significantly affect what content a RAG system retrieves.",
        isLie: false,
        conceptId: "retrieval",
      },
      {
        text: "Hallucination is impossible in a properly configured RAG system because the model only reads the retrieved text.",
        isLie: true,
        conceptId: "trust_reasoning",
      },
      {
        text: "An AI agent with tool access can take real-world actions such as sending emails or modifying files.",
        isLie: false,
        conceptId: "tools_agents",
      },
    ],
  },
  {
    intro: "From an introductory article about how AI language models are trained:",
    claims: [
      {
        text: "LLMs are pre-trained on large text corpora using a next-token prediction objective.",
        isLie: false,
        conceptId: "foundations",
      },
      {
        text: "GPT-4 was trained exclusively on 45 terabytes of peer-reviewed scientific literature curated by OpenAI researchers.",
        isLie: true,
        conceptId: "evidence",
      },
      {
        text: "Scaling model size and dataset size generally improves performance, a trend studied in 'neural scaling laws' research.",
        isLie: false,
        conceptId: "foundations",
      },
      {
        text: "Once deployed, an LLM continuously updates its weights in real time based on user conversation feedback.",
        isLie: true,
        conceptId: "model_theory",
      },
      {
        text: "RLHF (Reinforcement Learning from Human Feedback) is used to align model outputs with human preferences.",
        isLie: false,
        conceptId: "foundations",
      },
    ],
  },
  {
    intro: "From a corporate AI deployment guide on safe enterprise use of LLMs:",
    claims: [
      {
        text: "High-stakes AI decisions should include a qualified human reviewer in the loop.",
        isLie: false,
        conceptId: "system_safety",
      },
      {
        text: "Constitutional AI, developed by Anthropic, is the technique first used to align AI with human values, originating in a 1997 NeurIPS paper.",
        isLie: true,
        conceptId: "evidence",
      },
      {
        text: "Safety fine-tuning can significantly reduce but not fully prevent harmful outputs from a language model.",
        isLie: false,
        conceptId: "system_safety",
      },
      {
        text: "Deploying an LLM with role-based access controls limits what data the model can retrieve and act upon.",
        isLie: false,
        conceptId: "system_safety",
      },
      {
        text: "Any LLM that passes a published safety benchmark is certified safe for all enterprise deployment contexts.",
        isLie: true,
        conceptId: "system_safety",
      },
    ],
  },
];
